import { useState, useEffect, useCallback } from 'react';
import { useSafeUserId } from '@/hooks/useAuthGuard';
import { supabase } from '@/services/supabase';
import { SRSDataService, type DatabaseSRSCard } from '@/services/srsData';
import { useLanguageStore } from '@/stores/useLanguageStore';

// SRSStatistics interface removed - using dynamic data from transformations

interface SRSWord {
  id: string;
  word: string;
  translation?: string;
  currentState: string;
  understandingLevel: number;
  contextSentence?: string;
  totalCorrectUses: number;
  totalIncorrectUses: number;
  easiness_factor?: number;
  interval_days?: number;
  next_review_date?: string;
  last_reviewed_at?: string | null;
  first_encountered_at?: string;
  created_at?: string;
}

interface UseFixedSRSDataState {
  totalWords: number;
  wordsLearning: number;
  wordsMastered: number;
  wordsStruggling: number;
  averageUnderstanding: number;
  hasData: boolean;
  isLoading: boolean;
  error: string | null;
  // Additional properties for compatibility
  allWords: SRSWord[];
  dueWords: SRSWord[];
  wordsByLevel: any;
  chartData: {
    understandingDistribution: any[];
    stateDistribution: any[];
    difficultyProgress: any[];
  };
  insights: any[];
  progressStats: any;
}

export function useFixedSRSData() {
  const userId = useSafeUserId();
  const { targetLanguage } = useLanguageStore();
  const [state, setState] = useState<UseFixedSRSDataState>({
    totalWords: 0,
    wordsLearning: 0,
    wordsMastered: 0,
    wordsStruggling: 0,
    averageUnderstanding: 0,
    hasData: false,
    isLoading: true,
    error: null,
    allWords: [],
    dueWords: [],
    wordsByLevel: null,
    chartData: {
      understandingDistribution: [],
      stateDistribution: [],
      difficultyProgress: []
    },
    insights: [],
    progressStats: null
  });

  const fetchSRSData = useCallback(async () => {
    // Auth guard ensures userId is valid or null
    if (!userId) {
      setState(prev => ({ ...prev, isLoading: false, hasData: false }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Use the same data source as conversation planner for consistency
      const [statsResponse, dueCardsResponse] = await Promise.all([
        supabase.rpc('get_srs_statistics_with_defaults', {
          p_user_id: userId,
          p_language_code: targetLanguage
        }),
        supabase.rpc('get_srs_cards_due_for_review', {
          p_user_id: userId,
          p_language_code: targetLanguage,
          p_limit: 100
        })
      ]);

      console.log('🔍 [SRS DATA DEBUG] Raw responses:', {
        statsResponse: statsResponse.data,
        dueCardsResponse: dueCardsResponse.data,
        userId: userId.substring(0, 8)
      });

      // Handle statistics response
      if (statsResponse.error) {
        console.error('Error fetching SRS statistics:', statsResponse.error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: statsResponse.error.message,
          hasData: false
        }));
        return;
      }

      // Handle due cards response (consistent with conversation planner)
      if (dueCardsResponse.error) {
        console.error('Error fetching due cards:', dueCardsResponse.error);
        // Continue with stats only, just log the due cards error
        console.warn('Due cards data unavailable, continuing with stats only');
      }

      // Transform the statistics using the new service
      const transformedStats = SRSDataService.transformStatistics(statsResponse.data);

      // Handle due cards response structure (from get_srs_cards_due_for_review)
      const dueCardsArray = dueCardsResponse.data || [];

      // Transform due cards data using the new service
      const allWords: SRSWord[] = dueCardsArray.map((card: any) => {
        const dbCard = card as DatabaseSRSCard;
        const transformedCard = SRSDataService.transformCard(dbCard);

        return {
          id: transformedCard.id,
          word: transformedCard.word,
          translation: (transformedCard.translation && transformedCard.translation !== 'No translation') ? transformedCard.translation : undefined,
          currentState: transformedCard.currentState,
          understandingLevel: transformedCard.understandingLevel,
          contextSentence: transformedCard.contextSentence || undefined,
          totalCorrectUses: transformedCard.totalCorrectUses,
          totalIncorrectUses: transformedCard.totalIncorrectUses,
          easiness_factor: transformedCard.easeFactor,
          interval_days: transformedCard.intervalDays,
          next_review_date: transformedCard.nextReview,
          last_reviewed_at: transformedCard.lastReviewed,
          first_encountered_at: transformedCard.firstEncountered,
          created_at: transformedCard.firstEncountered
        };
      });
      
      // Use the transformed statistics
      const totalCards = transformedStats.totalCards;
      const cardsByState = {
        NEW: transformedStats.newCards,
        CONSOLIDATION: transformedStats.consolidationCards,
        RETENTION: transformedStats.retentionCards,
        LAPSED: transformedStats.lapsedCards
      };

      const hasData = totalCards > 0;

      const progressStats = {
        totalCards,
        averageRetention: transformedStats.averageRetention,
        dueToday: dueCardsArray.length, // Use actual count from get_srs_cards_due_for_review
        studyStreak: transformedStats.studyStreak,
        masteredWords: transformedStats.masteredWords,
        strugglingWords: transformedStats.strugglingWords,
        retentionPercentage: transformedStats.averageRetention
      };

      console.log('🔍 [SRS DATA DEBUG] Progress stats created:', {
        dueCardsArrayLength: dueCardsArray.length,
        transformedStatsDueToday: transformedStats.dueToday,
        finalProgressStatsDueToday: progressStats.dueToday,
        progressStats
      });
      
      // Use the actual due cards from get_srs_cards_due_for_review (same as conversation planner)
      const dueWords = dueCardsArray || [];

      // Group words by understanding level for wordsByLevel
      const wordsByLevel = {
        beginner: allWords.filter(w => w.understandingLevel < 30),
        intermediate: allWords.filter(w => w.understandingLevel >= 30 && w.understandingLevel < 70),
        advanced: allWords.filter(w => w.understandingLevel >= 70 && w.understandingLevel < 90),
        mastered: allWords.filter(w => w.understandingLevel >= 90 || w.currentState === 'RETENTION')
      };

      setState({
        totalWords: totalCards,
        wordsLearning: cardsByState.NEW + cardsByState.CONSOLIDATION,
        wordsMastered: cardsByState.RETENTION,
        wordsStruggling: cardsByState.LAPSED,
        averageUnderstanding: totalCards > 0 ?
          Math.round(((cardsByState.RETENTION || 0) + (cardsByState.CONSOLIDATION || 0) * 0.7 + (cardsByState.NEW || 0) * 0.3) / totalCards * 100) : 0,
        hasData,
        isLoading: false,
        error: null,
        allWords: allWords,
        dueWords: dueWords,
        wordsByLevel: wordsByLevel,
        chartData: {
          understandingDistribution: [],
          stateDistribution: [],
          difficultyProgress: []
        },
        insights: [],
        progressStats
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch SRS data';
      console.error('Error in fetchSRSData:', error);
      setState(prev => ({
        ...prev,
        totalWords: 0,
        wordsLearning: 0,
        wordsMastered: 0,
        wordsStruggling: 0,
        averageUnderstanding: 0,
        isLoading: false,
        error: errorMessage,
        hasData: false,
        allWords: [],
        dueWords: [],
        wordsByLevel: {
          beginner: [],
          intermediate: [],
          advanced: [],
          mastered: []
        },
        chartData: {
          understandingDistribution: [],
          stateDistribution: [],
          difficultyProgress: []
        },
        insights: [],
        progressStats: {
          totalCards: 0,
          averageRetention: 0,
          dueToday: 0,
          studyStreak: 0,
          masteredWords: 0,
          strugglingWords: 0,
          retentionPercentage: 0
        }
      }));
    }
  }, [userId, targetLanguage]);
  
  useEffect(() => {
    // Only fetch when userId is available (auth guard ensures this is valid)
    if (userId) {
      fetchSRSData();
    }
  }, [fetchSRSData, userId]);

  // Auto-update the due count (e.g. the sidebar "Daily Review" badge) when SRS
  // cards land in the DB. Cards are created server-side during chat, so without
  // this the badge stays frozen at its mount-time value until a full reload.
  // Inserts arrive in bursts, so debounce to a single refetch.
  useEffect(() => {
    if (!userId) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        fetchSRSData();
      }, 700);
    };

    const channel = supabase
      .channel(`fixed-srs-cards-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'srs_cards_minimal', filter: `user_id=eq.${userId}` },
        scheduleRefetch
      )
      // Grammar/sentence (extended) cards live in a separate table; without this
      // their reviews never refresh the due-count badge.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'srs_cards_extended', filter: `user_id=eq.${userId}` },
        scheduleRefetch
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [userId, fetchSRSData]);

  const createSampleData = async () => {
    // This would typically create sample SRS cards, but we rely on the database function
    await fetchSRSData();
  };

  return {
    ...state,
    refetch: fetchSRSData,
    createSampleData
  };
}